import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ChooseLogo } from '@/components/ui/choose-logo';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useUser } from '@/hooks/use-user';
import {
  DealListSort,
  DealSalesScope,
  GetDealsListDocument,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import {
  DEALS_PAGE_SIZE,
  DEAL_TABS,
  TAB_COUNT_KEYS,
  dealsListInput,
  dealsListState,
  setDealsPage,
  setDealsSales,
  setDealsSearchText,
  setDealsSearch,
  selectedManagerIds,
  setDealsTab,
  toggleDealsSort,
} from '@/states/deals-list.state';
import { useQuery } from '@apollo/client/react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  Search,
  X,
} from 'lucide-react';

import { DealRow } from './deal-row';
import { DealsTabs } from './deals-tabs';
import { SalesFilter } from './sales-filter';

export function DealsTable() {
  const { t } = useTranslation();
  const user = useUser();
  const state = useAtomValue(dealsListState);
  const input = useAtomValue(dealsListInput);
  const setTab = useSetAtom(setDealsTab);
  const setPage = useSetAtom(setDealsPage);
  const setSales = useSetAtom(setDealsSales);
  const setSearchText = useSetAtom(setDealsSearchText);
  const setSearch = useSetAtom(setDealsSearch);
  const toggleSort = useSetAtom(toggleDealsSort);
  // ME is resolved by the API; no browser-side identity guess or ALL fallback.
  const { data, previousData, loading, error, refetch } = useQuery(
    GetDealsListDocument,
    {
      variables: { input },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    }
  );
  // The directory is global; retain it across filters while never retaining old rows.
  const managers = (data ?? previousData)?.listDeals.managers ?? [];
  const resettingSales =
    managers.length === 0 && state.sales.salesScope !== DealSalesScope.Me;
  const result = !loading && !error ? data?.listDeals : undefined;
  const total = result?.counts[TAB_COUNT_KEYS[state.tab]] ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / DEALS_PAGE_SIZE));
  const correctingPage = result !== undefined && state.page > lastPage;
  const list = correctingPage || resettingSales ? undefined : result;
  useEffect(() => {
    if (correctingPage && !resettingSales) setPage(lastPage);
  }, [correctingPage, resettingSales, lastPage, setPage]);
  const tracked = useRef({
    viewed: false,
    tab: state.tab,
    sales: JSON.stringify(state.sales),
    search: state.search,
  });
  useEffect(() => {
    // Only the settled response has counts for the current filters/search.
    if (!list || state.searchText.trim() !== state.search) return;
    const previous = tracked.current;
    const sales = JSON.stringify(state.sales);
    if (!previous.viewed) {
      trackEvent('DEALS_LIST_VIEWED', {
        tab: state.tab,
        salesFilter:
          state.sales.salesScope === DealSalesScope.Me
            ? 'me'
            : state.sales.salesScope === DealSalesScope.All
              ? 'all'
              : 'custom',
        dealCount: list.counts[TAB_COUNT_KEYS[state.tab]],
      });
    }
    if (previous.tab !== state.tab) {
      trackEvent('DEALS_LIST_TAB_SELECTED', {
        tab: state.tab,
        dealCount: list.counts[TAB_COUNT_KEYS[state.tab]],
      });
    }
    if (previous.sales !== sales) {
      const selected = selectedManagerIds(state.sales, list.managers);
      const me = list.managers.filter((manager) => manager.isMe);
      trackEvent('DEALS_LIST_SALES_FILTER_CHANGED', {
        selectedCount: selected.length,
        isMeOnly:
          state.sales.salesScope === DealSalesScope.Me ||
          (me.length > 0 &&
            selected.length === me.length &&
            me.every((manager) => selected.includes(manager.id))),
      });
    }
    if (previous.search !== state.search) {
      trackEvent('DEALS_LIST_SEARCHED', {
        queryLength: state.search.length,
        resultCount:
          list.counts.notStarted + list.counts.inProgress + list.counts.closed,
      });
    }
    tracked.current = {
      viewed: true,
      tab: state.tab,
      sales,
      search: state.search,
    };
  }, [list, state.tab, state.sales, state.search, state.searchText]);
  useEffect(() => {
    const search = state.searchText.trim();
    if (search === state.search) return;
    if (!search) {
      setSearch('');
      return;
    }
    const timeout = setTimeout(() => setSearch(search), 300);
    return () => clearTimeout(timeout);
  }, [state.searchText, state.search, setSearch]);
  const sortLabel = t(
    input.sort === DealListSort.Asc
      ? 'deals-list.nearestFirst'
      : 'deals-list.furthestFirst'
  );
  useEffect(() => {
    document.title = t('deals-list.title');
  }, [t]);

  return (
    <TooltipProvider>
      <Tabs
        value={state.tab}
        onValueChange={(value) => {
          const tab = DEAL_TABS.find((item) => item === value);
          if (tab) setTab(tab);
        }}
      >
        <header className="flex h-[72px] items-center justify-between border-b px-6 sm:px-10">
          <div className="flex items-center gap-3 font-semibold">
            <ChooseLogo width={32} height={32} />
            <span>Smart Offer</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Avatar className="h-7 w-7 text-xs">
              <AvatarImage src={user?.providerUser.picture} alt="" />
              <AvatarFallback>
                {user?.providerUser.name?.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span>{user?.providerUser.name}</span>
          </div>
        </header>
        <main
          className="px-6 py-6 sm:px-10"
          data-filter-category={
            resettingSales ? 'me' : state.sales.salesScope.toLowerCase()
          }
          data-selected-count={selectedManagerIds(state.sales, managers).length}
          data-query-length={state.search.length}
          data-result-count={
            list
              ? list.counts.notStarted +
                list.counts.inProgress +
                list.counts.closed
              : undefined
          }
        >
          <h1 className="mb-5 text-2xl font-bold">{t('deals-list.title')}</h1>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <DealsTabs counts={list?.counts} />
            <SalesFilter
              selection={state.sales}
              managers={managers}
              onChange={setSales}
            />
            <div className="relative ml-auto w-full sm:w-60">
              <Search
                className="absolute left-3 top-3 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                className="h-10 pl-9 pr-9"
                value={state.searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={t('deals-list.search')}
                aria-label={t('deals-list.search')}
              />
              {state.searchText && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0"
                  aria-label={t('deals-list.clearSearch')}
                  onClick={() => setSearchText('')}
                >
                  <X />
                </Button>
              )}
            </div>
          </div>
          <TabsContent
            value={state.tab}
            aria-busy={loading || correctingPage || resettingSales}
          >
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="px-5">
                    <div className="flex items-center gap-4">
                      <span>{t('deals-list.deal')}</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleSort()}
                            aria-label={`${t('deals-list.launch')}: ${sortLabel}`}
                          >
                            {t('deals-list.launch')}
                            {input.sort === DealListSort.Asc ? (
                              <ArrowUpNarrowWide />
                            ) : (
                              <ArrowDownWideNarrow />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{sortLabel}</TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                  <TableHead className="px-5">
                    {t('deals-list.statusLabel')}
                  </TableHead>
                  <TableHead className="px-5 text-right">
                    {t('deals-list.actionsLabel')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list?.items.map((deal) => (
                  <DealRow key={deal.id} deal={deal} />
                ))}
              </TableBody>
            </Table>
            {(loading || correctingPage || resettingSales) && (
              <p
                role="status"
                className="p-10 text-center text-muted-foreground"
              >
                {t('deals-list.loading')}
              </p>
            )}
            {error && (
              <div role="alert" className="p-10 text-center">
                <p>{t('deals-list.error')}</p>
                <Button
                  variant="outline"
                  className="mt-3"
                  onClick={() => {
                    void refetch().catch(() => {});
                  }}
                >
                  {t('deals-list.retry')}
                </Button>
              </div>
            )}
            {list && list.items.length === 0 && (
              <p
                role="status"
                className="p-10 text-center text-muted-foreground"
              >
                {t('deals-list.empty')}
              </p>
            )}
            {list && (
              <nav
                aria-label={t('deals-list.pagination')}
                className="mt-4 flex items-center justify-end gap-3 text-sm"
              >
                <Button
                  variant="outline"
                  disabled={state.page === 1}
                  onClick={() => setPage(state.page - 1)}
                >
                  {t('deals-list.previous')}
                </Button>
                <span>
                  {t('deals-list.page', {
                    page: state.page,
                    count: Math.max(1, Math.ceil(total / DEALS_PAGE_SIZE)),
                  })}
                </span>
                <Button
                  variant="outline"
                  disabled={state.page * DEALS_PAGE_SIZE >= total}
                  onClick={() => setPage(state.page + 1)}
                >
                  {t('deals-list.next')}
                </Button>
              </nav>
            )}
          </TabsContent>
        </main>
      </Tabs>
    </TooltipProvider>
  );
}
